import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ProductsService } from '../products/products.service';
import { BaseCalculPrixAchat } from '../parametre-document/parametre-doc-api-normalize';
import { computeCmupWeightedAverage, resolvePrixEntreeUnitaireForCmup } from './achat-cmup.util';

export type AchatCmupLineItem = {
    product_id?: number | null;
    designation?: string | null;
    priceHT?: number | string | null;
    prixUnitaireRef?: number | string | null;
    quantity?: number | string | null;
    remise?: number | string | null;
    prix_achat?: number | string | null;
};

export type AchatCmupUpdateContext = {
    stationId: number;
    clientId: number;
    majPrixAchatProduit: boolean;
    baseCalculPrixAchat: BaseCalculPrixAchat;
    resolveStockQty: (item: AchatCmupLineItem, product?: any) => number;
};

/** Ligne document ERP → item CMUP (prix_achat = saisie, prix_ht = référence catalogue). */
export function mapDocumentLigneToAchatCmupItem(ligne: any): AchatCmupLineItem | null {
    const productId = Number(ligne?.product_id);
    if (!Number.isFinite(productId) || productId <= 0) {
        return null;
    }
    const ref = Number(ligne?.prix_ht) || 0;
    const saisie = Number(ligne?.prix_achat ?? ligne?.prix_ht) || 0;
    return {
        product_id: productId,
        designation: ligne?.libelle ?? ligne?.designation,
        priceHT: saisie,
        prixUnitaireRef: ref > 0 ? ref : saisie,
        quantity: ligne?.qnt ?? ligne?.quantity,
        remise: ligne?.remise ?? 0,
    };
}

export function isAchatLinePrixHtModified(item: AchatCmupLineItem): boolean {
    const current = Number(item.priceHT);
    const ref = Number(item.prixUnitaireRef);
    if (!Number.isFinite(current)) {
        return false;
    }
    if (!Number.isFinite(ref) || ref <= 0) {
        return true;
    }
    return Math.abs(current - ref) > 0.001;
}

@Injectable({ providedIn: 'root' })
export class AchatCmupProductUpdateService {
    constructor(private readonly productsService: ProductsService) {}

    updateProductsFromLines(
        items: AchatCmupLineItem[],
        ctx: AchatCmupUpdateContext
    ): Observable<unknown[]> {
        const productItems = (items || []).filter((i) => {
            const pid = Number(i.product_id);
            return Number.isFinite(pid) && pid > 0;
        });
        if (!productItems.length) {
            return of([]);
        }
        const putIds = { station_id: ctx.stationId, client_id: ctx.clientId };
        const updates = productItems.map((item) =>
            this.productsService.getDetailsProduct(item.product_id!).pipe(
                catchError(() => of({ data: {} } as any)),
                switchMap((res: any) => this.patchOneProduct(item, res?.data ?? res, ctx, putIds))
            )
        );
        return forkJoin(updates);
    }

    private patchOneProduct(
        item: AchatCmupLineItem,
        product: any,
        ctx: AchatCmupUpdateContext,
        putIds: { station_id: number; client_id: number }
    ): Observable<unknown> {
        const lignePu = this.getPrixEntreeNet(item, product, ctx);
        const valueToSend = this.computeCmup(product, item, ctx);
        if (valueToSend === null) {
            return of(null);
        }

        const resolvePutIds = (productData: any) => {
            const p = productData?.data ?? productData ?? product;
            return {
                station_id: Number(p?.station_id) || putIds.station_id,
                client_id: Number(p?.client_id) || putIds.client_id,
            };
        };

        const fullPut = (productData: any, payload: Record<string, unknown>) => {
            const ids = resolvePutIds(productData);
            if (!ids.station_id || !ids.client_id) {
                return of(null);
            }
            return this.productsService.updateProduct(
                item.product_id!,
                { ...(productData?.data ?? productData), ...payload },
                ids
            );
        };

        const updateValeurUnitaireCmup = () =>
            this.productsService.updateProductValeurUnitaire(item.product_id!, valueToSend, putIds).pipe(
                catchError(() =>
                    this.productsService.getDetailsProduct(item.product_id!).pipe(
                        switchMap((productRes: any) => {
                            const productData = productRes?.data ?? productRes;
                            return fullPut(productData, { valeur_unitaire: valueToSend });
                        }),
                        catchError(() => of(null))
                    )
                )
            );

        if (!ctx.majPrixAchatProduit) {
            return updateValeurUnitaireCmup().pipe(
                switchMap((vuRes) => {
                    if (!isAchatLinePrixHtModified(item)) {
                        return of(vuRes);
                    }
                    return this.productsService.getDetailsProduct(item.product_id!).pipe(
                        switchMap((productRes: any) => {
                            const productData = productRes?.data ?? productRes;
                            return fullPut(productData, { prix_unitaires: lignePu });
                        }),
                        catchError(() => of(vuRes))
                    );
                })
            );
        }

        return fullPut(product, { valeur_unitaire: valueToSend, prix_unitaires: lignePu }).pipe(
            catchError(() =>
                this.productsService.getDetailsProduct(item.product_id!).pipe(
                    switchMap((productRes: any) => {
                        const productData = productRes?.data ?? productRes;
                        const lu = this.getPrixEntreeNet(item, productData, ctx);
                        return fullPut(productData, {
                            valeur_unitaire: valueToSend,
                            prix_unitaires: lu,
                        });
                    }),
                    catchError(() => of(null))
                )
            )
        );
    }

    /** Valeur stockée pour le CMUP : valeur_unitaire ERP, repli achat_ht. */
    private extractValeurUnitaireProduit(product: any): number {
        const vu = parseFloat(String(product?.valeur_unitaire ?? '').replace(',', '.'));
        if (Number.isFinite(vu) && vu >= 0) {
            return vu;
        }
        const achat = parseFloat(String(product?.achat_ht ?? product?.prix_achat ?? '').replace(',', '.'));
        if (Number.isFinite(achat) && achat >= 0) {
            return achat;
        }
        return 0;
    }

    private getPrixEntreeNet(item: AchatCmupLineItem, product: any, ctx: AchatCmupUpdateContext): number {
        return resolvePrixEntreeUnitaireForCmup(item, product, ctx.baseCalculPrixAchat);
    }

    private computeCmup(product: any, item: AchatCmupLineItem, ctx: AchatCmupUpdateContext): number | null {
        const prixEntreeUnitaire = this.getPrixEntreeNet(item, product, ctx);
        const quantiteEntree =
            item.quantity !== null && item.quantity !== undefined ? Number(item.quantity) : 0;
        const valeur_unitaire_precedent = this.extractValeurUnitaireProduit(product);
        const quantite_stock = ctx.resolveStockQty(item, product);
        return computeCmupWeightedAverage({
            valeurPrecedent: valeur_unitaire_precedent,
            quantiteStock: quantite_stock,
            prixEntreeUnitaire,
            quantiteEntree,
        });
    }
}
